import { ChangeDetectionStrategy, Component, DestroyRef, inject, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, Subject, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { UsuarioService } from '@/core/api/sicom-api/sistema/usuario/usuario.service';
import { SasUsuarioSearchResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';

@Component({
    selector: 'app-pesquisa-usuario',
    standalone: true,
    imports: [FormsModule, ButtonModule, InputTextModule, IconFieldModule, InputIconModule, TableModule, TagModule, TooltipModule],
    templateUrl: './pesquisa-usuario.html',
    styleUrl: './pesquisa-usuario.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Busca de usuários no SAS (com debounce) para vínculo; emite o usuário selecionado. */
export class PesquisaUsuario {
    private readonly usuarioService = inject(UsuarioService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly busca$ = new Subject<string>();

    selecionado = output<SasUsuarioSearchResponse>();

    protected termoBusca = '';
    protected readonly resultados = signal<SasUsuarioSearchResponse[]>([]);
    protected readonly carregando = signal(false);
    protected readonly buscaRealizada = signal(false);

    constructor() {
        this.busca$
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                switchMap((q) => {
                    this.carregando.set(true);
                    // catchError DENTRO do switchMap: uma falha zera o resultado sem encerrar o stream (buscas seguintes seguem funcionando).
                    return this.usuarioService.buscarSas(q, 0, 20).pipe(
                        catchError(() => {
                            this.resultados.set([]);
                            this.carregando.set(false);
                            return EMPTY;
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((page) => {
                this.resultados.set(page.content);
                this.carregando.set(false);
                this.buscaRealizada.set(true);
            });
    }

    protected onTermoChange(termo: string): void {
        if (termo.trim().length >= 2) {
            this.busca$.next(termo.trim());
        } else {
            this.resultados.set([]);
            this.buscaRealizada.set(false);
        }
    }

    protected selecionar(item: SasUsuarioSearchResponse): void {
        if (!item.jaVinculado) {
            this.selecionado.emit(item);
        }
    }

    protected statusSas(inStatus: string): 'success' | 'danger' | 'warn' {
        if (inStatus === 'A') return 'success';
        if (inStatus === 'I') return 'danger';
        return 'warn';
    }

    protected labelStatusSas(inStatus: string): string {
        if (inStatus === 'A') return 'Ativo';
        if (inStatus === 'I') return 'Inativo';
        return inStatus ?? '—';
    }

    /**
     * O que o vínculo vai fazer além do óbvio, ou `null` quando não há nada a avisar. Deixa a
     * consequência visível antes da escolha, em vez de o usuário descobrir depois.
     */
    protected rotuloEstado(item: SasUsuarioSearchResponse): string | null {
        if (!item.ehFuncionario) return 'Sem cadastro de funcionário';
        if (!item.temSas) return 'Sem login no legado';
        return null;
    }
}
